import Vue from 'vue'
import Vuex from 'vuex'
Vue.use(Vuex);
import createPersistedState from 'vuex-persistedstate'

function defaultAiAssistantState() {
    return {
        panelOpen: false,
        mode: 'suggestion',
        messages: [],
        draft: '',
        /** Pending uploads / captures before send (max 5); each { id, base64, mime, previewDataUrl } */
        pendingImages: [],
        threadHistory: [],
        /** Stable id for the open conversation so History can show it without "New chat" first */
        sessionHistoryId: null,
    };
}

/** Strip large image data URLs before localStorage — keeps quota; thumbnails stay in RAM until refresh */
function shrinkAiAssistantForPersistence(ai) {
    const d = defaultAiAssistantState();
    if (!ai || typeof ai !== 'object') {
        return d;
    }
    const stripPreviews = (msgs) => (Array.isArray(msgs) ? msgs : []).map((m) => {
        if (!m || typeof m !== 'object') {
            return m;
        }
        const out = { ...m };
        delete out.screenCapturePreview;
        delete out.screenCaptureDataUrl;
        delete out.attachmentPreviews;
        delete out.attachmentFullDataUrls;
        delete out._streaming;
        return out;
    });
    return {
        panelOpen: !!ai.panelOpen,
        mode: ai.mode === 'action' ? 'action' : 'suggestion',
        messages: stripPreviews(ai.messages),
        draft: typeof ai.draft === 'string' ? ai.draft : '',
        pendingImages: [],
        threadHistory: (Array.isArray(ai.threadHistory) ? ai.threadHistory : []).slice(0, 20).map((h) => ({
            ...h,
            messages: stripPreviews(h.messages),
        })),
        sessionHistoryId: typeof ai.sessionHistoryId === 'string' && ai.sessionHistoryId !== ''
            ? ai.sessionHistoryId
            : null,
    };
}

const store = new Vuex.Store({
    state: {
        auth: {
            status: false,
            id: '',
            uuid: '',
            name: '',
            email: '',
            username: '',
            avatar: '',
            roles: [],
            color_theme: '',
            direction: '',
            locale: '',
            sidebar: '',
            two_factor_code: null,
            permissions: [],
            last_activity: ''
        },
        config: {},
        ReceiptFormat: "",
        academic_sessions: [],
        default_academic_session: {},
        aiAssistant: defaultAiAssistantState(),
    },
    mutations: {
        setAuthUserDetail (state, auth) {
            for (let key of Object.keys(auth)) {
                state.auth[key] = auth[key] !== null ? auth[key] : '';
            }
            if ('avatar' in auth)
                state.auth.avatar = auth.avatar !== null ? auth.avatar : '';
            state.auth.status = true;
            state.auth.roles = Array.isArray(auth.roles) ? auth.roles : [];
            state.auth.permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
            state.auth.last_activity = moment().format();
        },
        resetAuthUserDetail (state) {
            for (let key of Object.keys(state.auth)) {
                state.auth[key] = '';
            }
            state.auth.status = false;
            state.auth.roles = [];
            state.auth.permissions = [];
            state.auth.last_activity = null;
            state.aiAssistant = defaultAiAssistantState();
            Vue.cookie.delete('sipinor_admin_auth_token');
            Vue.cookie.delete('auth_token');
            axios.defaults.headers.common['Authorization'] = null;
        },
        setConfig (state, config) {
            for (let key of Object.keys(config)) {
                state.config[key] = config[key];
            }
        },
        resetConfig (state) {
            for (let key of Object.keys(state.config)) {
                delete state.config[key];
            }
        },
        resetTwoFactorCode (state) {
            state.auth.two_factor_code = '';
        },
        setLastActivity(state) {
            state.auth.last_activity = moment().format();
        },
        setAcademicSession(state, data) {
            state.academic_sessions = data;
        },
        setReceiptFormat(state, data) {
            state.ReceiptFormat = data;
        },
        resetAcademicSession(state) {
            state.academic_sessions = [];
        },
        setDefaultAcademicSession(state, data) {
            state.default_academic_session = data;
        },
        resetDefaultAcademicSession(state) {
            state.default_academic_session = {};
        },
        aiAssistantSetPanelOpen(state, v) {
            state.aiAssistant.panelOpen = !!v;
        },
        aiAssistantSetMode(state, v) {
            state.aiAssistant.mode = v === 'action' ? 'action' : 'suggestion';
        },
        aiAssistantSetDraft(state, v) {
            state.aiAssistant.draft = v != null ? String(v) : '';
        },
        aiAssistantSetMessages(state, v) {
            state.aiAssistant.messages = Array.isArray(v) ? v : [];
        },
        aiAssistantPushMessage(state, m) {
            state.aiAssistant.messages.push(m);
        },
        aiAssistantSetMessageContent(state, { index, content }) {
            const msgs = state.aiAssistant.messages;
            if (!Array.isArray(msgs) || index < 0 || index >= msgs.length) {
                return;
            }
            const row = msgs[index];
            if (!row || typeof row !== 'object') {
                return;
            }
            const v = content != null ? String(content) : '';
            Vue.set(row, 'content', v);
        },
        aiAssistantAddPendingImage(state, item) {
            const list = Array.isArray(state.aiAssistant.pendingImages) ? state.aiAssistant.pendingImages : [];
            if (list.length >= 5 || !item || typeof item !== 'object') {
                return;
            }
            if (!item.id || !item.base64 || !item.previewDataUrl) {
                return;
            }
            state.aiAssistant.pendingImages = list.concat([item]).slice(0, 5);
        },
        aiAssistantRemovePendingImage(state, id) {
            const list = Array.isArray(state.aiAssistant.pendingImages) ? state.aiAssistant.pendingImages : [];
            state.aiAssistant.pendingImages = list.filter((x) => x && x.id !== id);
        },
        aiAssistantClearPendingImages(state) {
            state.aiAssistant.pendingImages = [];
        },
        aiAssistantSetThreadHistory(state, v) {
            state.aiAssistant.threadHistory = Array.isArray(v) ? v.slice(0, 20) : [];
        },
        aiAssistantSetSessionHistoryId(state, v) {
            state.aiAssistant.sessionHistoryId = v != null && String(v) !== '' ? String(v) : null;
        },
        aiAssistantEnsureShape(state) {
            const d = defaultAiAssistantState();
            const cur = state.aiAssistant;
            if (!cur || typeof cur !== 'object') {
                state.aiAssistant = d;
                return;
            }
            let pendingImages = [];
            if (Array.isArray(cur.pendingImages)) {
                pendingImages = cur.pendingImages
                    .filter((x) => x && x.id && x.base64 && x.previewDataUrl)
                    .slice(0, 5);
            } else if (cur.pendingScreenshot && typeof cur.pendingScreenshot === 'object' && cur.pendingScreenshot.previewDataUrl) {
                pendingImages = [{
                    id: `legacy-${Date.now()}`,
                    base64: cur.pendingScreenshot.base64,
                    mime: cur.pendingScreenshot.mime || 'image/jpeg',
                    previewDataUrl: cur.pendingScreenshot.previewDataUrl,
                }];
            }
            state.aiAssistant = {
                panelOpen: !!cur.panelOpen,
                mode: cur.mode === 'action' ? 'action' : 'suggestion',
                messages: Array.isArray(cur.messages) ? cur.messages : [],
                draft: typeof cur.draft === 'string' ? cur.draft : '',
                pendingImages,
                threadHistory: Array.isArray(cur.threadHistory) ? cur.threadHistory.slice(0, 20) : [],
                sessionHistoryId: typeof cur.sessionHistoryId === 'string' && cur.sessionHistoryId !== ''
                    ? cur.sessionHistoryId
                    : null,
            };
        },
    },
    actions: {
        setAuthUserDetail ({ commit }, auth) {
            commit('setAuthUserDetail',auth);
        },
        resetAuthUserDetail ({commit}){
            commit('resetAuthUserDetail');
        },
        setConfig ({ commit }, data) {
            commit('setConfig',data);
        },
        resetConfig({ commit }) {
            commit('resetConfig');
        },
        resetTwoFactorCode({ commit }) {
            commit('resetTwoFactorCode');
        },
        setLastActivity({ commit }) {
            commit('setLastActivity');
        },
        setAcademicSession({ commit }, data) {
            commit('setAcademicSession',data);
        },
        setReceiptFormat({ commit }, data) {
            commit('setReceiptFormat',data);
        },
        resetAcademicSession({ commit }) {
            commit('resetAcademicSession');
        },
        setDefaultAcademicSession({ commit }, data) {
            commit('setDefaultAcademicSession',data);
        },
        resetDefaultAcademicSession({ commit }) {
            commit('resetDefaultAcademicSession');
        }
    },
    getters: {
        getAuthUser: (state) => (name) => {
            return state.auth[name];
        },
        getReceiptFormat: (state) => {
            return state.ReceiptFormat;
        },
        getAuthStatus: (state) => {
            return state.auth.status;
        },
        getAuthUserRoles: (state) => {
            const r = state.auth.roles;
            return Array.isArray(r) ? r.toString() : '';
        },
        hasRole: (state) => (name) => {
            const r = state.auth.roles;
            if (!Array.isArray(r)) return false;
            return r.indexOf(name) >= 0;
        },
        hasAnyRole: (state) => (roles) => {
            const r = state.auth.roles;
            if (!Array.isArray(r) || !Array.isArray(roles)) return false;
            return r.some(role => roles.indexOf(role) > -1);
        },
        hasNotAnyRole: (state) => (roles) => {
            const r = state.auth.roles;
            if (!Array.isArray(r) || !Array.isArray(roles)) return true;
            return r.every(role => roles.indexOf(role) < 0);
        },
        getConfig: (state) => (name) => {
            return state.config[name];
        },
        hasPermission: (state) => (name) => {
            const p = state.auth.permissions;
            if (!Array.isArray(p)) return false;
            return p.indexOf(name) > -1;
        },
        hasAnyPermission: (state) => (permissions) => {
            const p = state.auth.permissions;
            if (!Array.isArray(p) || !Array.isArray(permissions)) return false;
            return p.some(permission => permissions.indexOf(permission) > -1);
        },
        getLastActivity: (state) => {
            return state.auth.last_activity;
        },
        getDefaultRole: (state) => (name) => {
            return state.config.default_roles ? state.config.default_roles[name] : '';
        },
        getAcademicSessions: (state) => {
            return state.academic_sessions;
        },
        getDefaultAcademicSession: (state) => {
            return state.default_academic_session;
        }
    },
    plugins: [
        createPersistedState({
            key: 'Sipinor',
            reducer: (state) => ({
                auth: state.auth,
                config: state.config,
                ReceiptFormat: state.ReceiptFormat,
                academic_sessions: state.academic_sessions,
                default_academic_session: state.default_academic_session,
                aiAssistant: shrinkAiAssistantForPersistence(state.aiAssistant),
            }),
        }),
    ],
});

export default store;
